import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useEffect } from "react";
import {
  addToWishlist,
  removeFromWishlist,
  updateWishlistQty,
  clearWishlist,
} from "../store/wishlistSlice";
import { setWishlistCount } from "../store/apiCountsSlice";
import {
  addToWishlistAPI,
  removeFromWishlistAPI,
} from "../api/wishlistApiService";
import {
  removeFromWishlist as removeFromCookieWishlist,
  addToWishlist as addToCookieWishlist,
} from "../utilities/cookieUtils";

/**
 * Custom hook for wishlist management
 * Handles both local (cookies) and API-based (guest/user) wishlist operations
 * 
 * Flow:
 * - Random User: Uses cookies only
 * - Guest/User: Uses API, with cookies as backup
 */
export const useWishlist = () => {
  const dispatch = useDispatch();

  // Select auth and wishlist from Redux
  const userInfo = useSelector((state: any) => state.auth.userInfo);
  const items = useSelector((state: any) => state.wishlist.items);

  // Memoized wishlist count
  const wishlistCount = useMemo(() => items.length, [items]);

  // Sync from API when user logs in
  useEffect(() => {
    // Sync is handled by cartWishlistSyncService on login
    // No need to sync here
  }, [userInfo?.user_id || userInfo?.guest_id]); // Empty - sync happens in auth flow

  // Check if product is in wishlist
  const isWishlisted = useCallback(
    (mappingCode: string): boolean => {
      return items.some((item: any) => item.mappingCode === mappingCode);
    },
    [items]
  );

  // Add to wishlist (with API support)
  const handleAddToWishlist = useCallback(
    async (mappingCode: string) => {
      // API service handles user type detection from storage
      // It will use API for guest/user, return success for random users
      const result = await addToWishlistAPI(mappingCode);

      if (result.success) {
        // Update Redux
        dispatch(addToWishlist({ mappingCode }));
        addToCookieWishlist(mappingCode); // Keep cookies in sync as backup

        // Update Redux with count from API if user is logged in
        if ((userInfo?.user_id || userInfo?.guest_id) && result.count !== undefined) {
          dispatch(setWishlistCount(result.count));
        }
      }
      // Return API result so callers can implement optimistic UI and revert on failure
      return result;
    },
    [dispatch, userInfo]
  );

  // Remove from wishlist (with API support)
  const handleRemoveFromWishlist = useCallback(
    async (mappingCode: string) => {
      // API service handles user type detection from storage
      // It will use API for guest/user, return success for random users
      const result = await removeFromWishlistAPI(mappingCode);

      if (result.success) {
        // Remove from Redux
        dispatch(removeFromWishlist(mappingCode));
        removeFromCookieWishlist(mappingCode);

        // Update Redux with count from API if user is logged in
        if ((userInfo?.user_id || userInfo?.guest_id) && result.count !== undefined) {
          dispatch(setWishlistCount(result.count));
        }
      }
      // Return API result so callers can implement optimistic UI and revert on failure
      return result;
    },
    [dispatch, userInfo]
  );

  // Update quantity in wishlist
  const handleUpdateWishlistQty = useCallback(
    (mappingCode: string, qty: number) => {
      dispatch(updateWishlistQty({ mappingCode, qty }));
    },
    [dispatch]
  );

  // Clear entire wishlist
  const handleClearWishlist = useCallback(() => {
    dispatch(clearWishlist());
  }, [dispatch]);

  return {
    wishlist: items,
    items,
    wishlistCount,
    isWishlisted,
    addToWishlist: handleAddToWishlist,
    removeFromWishlist: handleRemoveFromWishlist,
    updateWishlistQty: handleUpdateWishlistQty,
    clearWishlist: handleClearWishlist,
  };
};
